<template>
  <div>
    <CModal :show.sync="activeBlock" centered :draggable="false">
      <template slot="header-wrapper">
        <div />
      </template>
      Etes-vous sûrs de vouloir bloquer/débloquer le statut de la sélection ?
      <template slot="footer">
        <CButton
          color="secondary"
          class="mr-2"
          @click="activeBlock = false"
        >
          Non
        </CButton>
        <CButton
          color="primary"
          @click="blockSelected(selectedIds)"
          data-cy="confirm-delete-button"
        >
          Oui
        </CButton>
      </template>
    </CModal>

    <ReferenceTableActions
      :table-name="refNameLower"
      manageButtonContent="Bloquer / Débloquer"
      :buttons="['create', 'template', 'upload', 'manage-button', 'export']"
      @create="() => (currentEditObj = {})"
      @template="downloadTemplate"
      @upload="uploadTemplate"
      @export="downloadExport"
      @manage-button="activeBlock = true"
      :disabledButtons="!selectedIds.length ? ['manage-button'] : []"
    >
      <template #new-btn-content v-if="refName === `Secteur d'activité`">
        Ajouter un nouveau secteur d'activité
      </template>
    </ReferenceTableActions>

    <tableComponent
      :url="refConfig.service.endpoints.base"
      :columns="columns"
      :headings="headings"
      :filterExcludeColumns="['actions']"
      ref="table"
      @row-selected="rowSelected"
      shouldAllowRowSelection
    >
      <template slot="row-selection-button">
        <div/>
      </template>
      <template #is_blocked="{ row }">
        {{ row.is_blocked ? "X" : "" }}
      </template>
      <template #actions="{ row }">
        <div class="d-flex flex-wrap gap-2">
          <CButton
            class="sm outline action-button"
            color="primary"
            size="sm"
            variant="outline"
            @click="currentEditObj = { ...row }"
          >
            <em class="fa fa-edit" />
            Modifier
          </CButton>
          <CButton
            class="action-button"
            color="danger"
            size="sm"
            @click="currentDeleteObj = row"
          >
            <em class="fa fa-close" />
            Supprimer
          </CButton>
        </div>
      </template>
    </tableComponent>

    <CForm @submit.prevent="save" v-if="currentEditObj">
      <CModal
        show
        @update:show="currentEditObj = null"
        :draggable="false"
        :resizable="false"
        centered
        :title="`${
          isCreate ? 'Créer un noveau' : 'Modifier un'
        } ${refNameLower}`"
      >
        <div class="d-flex flex-column gap-2">
          <CInput
            v-if="!isCreate"
            :value="currentEditObj[refConfig.code]"
            :label="headings[refConfig.code]"
            addInputClasses="mz-input"
            disabled
          />
          <CInput
            v-for="field in editFields"
            v-model.trim="currentEditObj[field]"
            :key="field"
            :label="headings[field] || field"
            addInputClasses="mz-input"
            required
          />
        </div>
        <template #footer>
          <div class="d-flex gap-2">
            <CButton color="secondary" @click="currentEditObj = null">
              Annuler
            </CButton>
            <CButton color="primary" type="submit">
              {{ isCreate ? "Créer" : "Modifier" }} un {{ refNameLower }}
            </CButton>
          </div>
        </template>
      </CModal>
    </CForm>

    <CModal :show.sync="currentDeleteObj" centered :draggable="false">
      <template #header-wrapper>
        <div />
      </template>
      <div v-if="currentDeleteObj">
        Etes-vous sûr de vouloir supprimer {{ currentDeleteObj.name_FR }} ?
      </div>
      <template #footer>
        <CButton
          color="secondary"
          class="mr-2"
          @click="currentDeleteObj = null"
        >
          Non
        </CButton>
        <CButton color="primary" @click="handleRemove">Oui</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { downloadFile } from "@/utils/request";
import { FilesService } from "@/services/files";
import ReferenceTableActions from "@/components/ReferenceTableActions";
import { SecteurActiviteService } from "@/services/secteurActivite";
import { SpecialiteFormationService } from "@/services/specialiteFormation";

export default {
  name: "RefTable",
  components: { ReferenceTableActions },
  data() {
    return {
      selectedIds: [],
      currentEditObj: null,
      currentDeleteObj: null,
      isUploading: false,
      activeBlock: false,
      editFields: ["name_FR", "name_EN", "name_ZH"],
    };
  },
  computed: {
    refName() {
      return this.$route.name;
    },
    refNameLower() {
      if (this.refName === "Spécialité formation") {
        return "spécialité";
      }
      return "secteurs d'activité";
    },
    refConfig() {
      return {
        "Secteur d'activité": {
          service: SecteurActiviteService,
          code: "code_secteur_activite",
        },
        "Spécialité formation": {
          service: SpecialiteFormationService,
          code: "code_specialite_formation",
        },
      }[this.refName];
    },
    headings() {
      const headings = { [this.refConfig.code]: "Code réponse" };
      const editHeadings = Object.fromEntries(
        this.editFields.map((field) => [
          field,
          `${this.refName} (${field.split("_").pop()})`,
        ])
      );
      return { ...headings, ...editHeadings };
    },
    columns() {
      return [...Object.keys(this.headings), "is_blocked", "actions"];
    },
    isCreate() {
      return this.currentEditObj && !this.currentEditObj._id;
    },
  },
  methods: {
    async blockSelected() {
      const { res } = await this.refConfig.service.block(this.selectedIds);
      if (res.status !== 200) alert("Something went wrong");
      this.activeBlock = false;
      this.refreshTable();
    },
    rowSelected(selected) {
      this.selectedIds = selected.map((item) => item._id);
    },
    refreshTable() {
      this.$refs.table.$refs.table.getData();
    },
    async save() {
      let response;
      if (this.isCreate) {
        response = await this.refConfig.service.create(this.currentEditObj);
      } else {
        response = await this.refConfig.service.update(this.currentEditObj);
      }
      const { res, data } = response;
      if (res.status !== 200) {
        return alert(data?.detail || "Something went wrong");
      }
      alert(
        `Successfully ${this.isCreate ? "created" : "modified"} ${
          this.refNameLower
        }`
      );
      this.currentEditObj = null;
      this.refreshTable();
    },
    async handleRemove() {
      await this.refConfig.service.delete(this.currentDeleteObj._id);
      this.refreshTable();
      this.currentDeleteObj = null;
    },
    downloadTemplate() {
      downloadFile({
        downloadURL: this.refConfig.service.endpoints.detail("template"),
      });
    },
    downloadExport() {
      downloadFile({
        downloadURL: this.refConfig.service.endpoints.detail("export"),
      });
    },
    async uploadTemplate(files) {
      this.isUploading = true;
      await FilesService.uploadFile({
        file: files[0],
        url: this.refConfig.service.endpoints.detail("template"),
        onError: (res) => {
          alert(res.detail);
          this.isUploading = false;
        },
        onSuccess: (res) => {
          alert(
            `Successfully inserted ${res.inserted_count} ${this.refNameLower}`
          );
        },
      });
      this.isUploading = false;
      this.refreshTable();
    },
  },
};
</script>
